// Enterprise Dashboard Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { warrantyService } from '../services/warranty';
import type { Warranty } from '../types/warranty';

// Cache busting utility - only for development
const getCacheBustedIconUrl = (iconName: string) => {
  // Only add timestamp in development
  if (import.meta.env.DEV) {
    const timestamp = Date.now();
    return `/icons/${iconName}.svg?v=${timestamp}`;
  }
  // In production, use normal path for proper caching
  return `/icons/${iconName}.svg`;
};

// Simple icon component for using SVG files with cache busting
const Icon = ({ name, className = "" }: { name: string; className?: string }) => {
  return (
    <img 
      src={getCacheBustedIconUrl(name)} 
      alt="" 
      className={`icon ${className}`}
      onError={(e) => {
        console.error(`Failed to load icon: ${name}.svg`);
        // Fallback to a different approach if needed
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

interface DashboardStats {
  totalWarranties: number;
  activeWarranties: number;
  expiringSoonWarranties: number;
  expiredWarranties: number;
  inactiveWarranties?: number;
  validWarranties?: number; // Currently protected (not expired)
}

const DashboardPage: React.FC = () => {
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalWarranties: 0,
    activeWarranties: 0,
    expiringSoonWarranties: 0,
    expiredWarranties: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const userEmail = localStorage.getItem('userEmail') || 'test@test.com';
      
      // Fetch comprehensive warranty statistics
      const statsData = await warrantyService.getWarrantyStatistics(userEmail);
      setStats({
        totalWarranties: statsData.totalWarranties || 0,
        activeWarranties: statsData.activeWarranties || 0,
        expiringSoonWarranties: statsData.expiringSoonWarranties || 0,
        expiredWarranties: statsData.expiredWarranties || 0,
        inactiveWarranties: statsData.inactiveWarranties || 0,
        validWarranties: (statsData.activeWarranties || 0) - (statsData.expiredWarranties || 0) // Calculate currently protected
      });

      // Fetch recent warranties for activity display
      const warrantiesData = await warrantyService.getUserWarranties(userEmail);
      // Convert WarrantyResponse[] to Warranty[] and show only 3 recent ones
      const convertedWarranties = warrantiesData.slice(0, 3).map(w => ({
        id: String(w.id || Math.random()),
        userId: w.userId,
        productName: w.productName || 'Unknown Product',
        brand: w.brand || 'Unknown Brand',
        modelNumber: w.modelNumber || 'N/A',
        purchaseDate: w.purchaseDate || '',
        purchaseStore: w.purchaseStore,
        warrantyExpiryDate: w.warrantyExpiryDate || '',
        status: (w.status || 'active') as 'active' | 'expiring' | 'expired',
        warrantyProvider: w.warrantyProvider || 'Unknown Provider',
        category: (w.category || 'other') as 'electronics' | 'appliances' | 'automotive' | 'home' | 'other',
        tags: typeof w.tags === 'string' ? w.tags.split(',').map(t => t.trim()).filter(Boolean) : (w.tags || []),
        entryMethod: (w.entryMethod || 'manual') as 'manual' | 'document-extraction' | 'auto-import',
        createdAt: w.createdAt,
        updatedAt: w.updatedAt,
        documentId: w.documentId
      }));
      setWarranties(convertedWarranties);

    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getExpiryText = (expiryDate: string, status: string) => {
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (status === 'expired') {
      return `Warranty expired ${Math.abs(diffDays)} days ago`;
    } else if (status === 'expiring') {
      return `Warranty expires in ${diffDays} days`;
    } else {
      const diffMonths = Math.floor(diffDays / 30);
      if (diffMonths > 0) {
        return `Warranty expires in ${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
      } else {
        return `Warranty expires in ${diffDays} days`;
      }
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'electronics': 
        return <img src="/icons/ocr-camera.svg" alt="Electronics" className="category-icon" />;
      case 'appliances': 
        return <img src="/icons/appliances.svg" alt="Appliances" className="category-icon" />;
      case 'automotive': 
        return <img src="/icons/enterprise-security.svg" alt="Automotive" className="category-icon" />;
      case 'home': 
        return <img src="/icons/cloud-storage.svg" alt="Home" className="category-icon" />;
      default: 
        return <img src="/icons/product.svg" alt="Product" className="category-icon" />;
    }
  };

  const getPortfolioHealthMessage = (stats: DashboardStats) => {
    const healthScore = stats.activeWarranties / Math.max(stats.totalWarranties, 1);
    
    if (stats.totalWarranties === 0) {
      return "Start building your warranty portfolio for better protection";
    } else if (healthScore >= 0.8) {
      return `Excellent! ${Math.round(healthScore * 100)}% of your warranties are active`;
    } else if (healthScore >= 0.6) {
      return `Good portfolio health with ${Math.round(healthScore * 100)}% active coverage`;
    } else {
      return `Consider renewing expired warranties for better protection`;
    }
  };

  const getPortfolioHealthStatus = (stats: DashboardStats) => {
    const healthScore = stats.activeWarranties / Math.max(stats.totalWarranties, 1);
    
    if (stats.totalWarranties === 0) return 'active';
    if (healthScore >= 0.8) return 'success';
    if (healthScore >= 0.6) return 'warning';
    return 'error';
  };

  const getPortfolioHealthLabel = (stats: DashboardStats) => {
    const healthScore = stats.activeWarranties / Math.max(stats.totalWarranties, 1);
    
    if (stats.totalWarranties === 0) return 'Get Started';
    if (healthScore >= 0.8) return 'Excellent';
    if (healthScore >= 0.6) return 'Good';
    return 'Needs Attention';
  };

  const getSmartTips = (stats: DashboardStats) => {
    const tips = [];
    
    if (stats.totalWarranties === 0) {
      tips.push("🚀 Start by adding your most valuable purchases");
      tips.push("📸 Keep digital copies of receipts for easy access");
    } else if (stats.expiringSoonWarranties > 0) {
      tips.push(`⚠️ ${stats.expiringSoonWarranties} warranties need immediate attention`);
      tips.push("📅 Set calendar reminders for renewal deadlines");
    } else if (stats.activeWarranties > 0) {
      tips.push("✅ Great job maintaining active coverage!");
      tips.push("📋 Consider adding more items to expand protection");
    }
    
    if (stats.expiredWarranties > 0) {
      tips.push(`🔄 Review ${stats.expiredWarranties} expired warranties for renewal options`);
    }
    
    // Always include a general tip
    tips.push("💡 Regular portfolio review ensures optimal protection");
    
    return (
      <ul className="sidebar-list">
        {tips.slice(0, 3).map((tip, index) => (
          <li key={index}>{tip}</li>
        ))}
      </ul>
    );
  };

  if (loading) {
    return (
      <div className="page-theme-list">
        <div className="main-content">
          <div className="content-container">
            <div className="loading-state">
              <h2>Loading your dashboard...</h2>
              <p>Please wait while we fetch your data.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-theme-list">
        <div className="main-content">
          <div className="content-container">
            <div className="error-state">
              <h2>Error Loading Dashboard</h2>
              <p>{error}</p>
              <button onClick={loadDashboardData} className="btn btn-primary">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-theme-list">
      {/* Page Header */}
      <header className="page-header">
        <div className="page-header-content">
          <div>
            <h1 className="page-title">
              <img src="/icons/smart-analytics.svg" alt="Dashboard" className="page-title-icon" />
              Dashboard
            </h1>
            <p className="page-subtitle">
              Welcome to your digital warranty locker - 
              <span className="tamil-accent">சுவடி</span>
            </p>
          </div>
          <div className="page-actions">
            <button className="btn btn-secondary btn-md">
              <img src="/icons/smart-analytics.svg" alt="" className="btn-icon" />
              Reports
            </button>
            <button 
              className="btn btn-primary btn-md"
              onClick={() => navigate('/warranty/intelligence')}
            >
              <Icon name="add-item" className="btn-icon" />
              🤖 AI Assistant
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        <div className="content-container">
          
          {/* Simple Top Metrics */}
          <div className="metrics-overview-simple">
            <div className="metrics-grid">
              <div className="metric-card metric-total">
                <div className="metric-icon">
                  <img src="/icons/smart-analytics.svg" alt="Total Items" className="metric-icon-img" />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{stats.activeWarranties}</div>
                  <div className="metric-label">Total Active Items</div>
                </div>
              </div>
              
              <div className="metric-card metric-valid">
                <div className="metric-icon">
                  <img src="/icons/warranty-shield.svg" alt="Protected" className="metric-icon-img" />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{Math.max(0, stats.activeWarranties - stats.expiredWarranties)}</div>
                  <div className="metric-label">Warranty Active</div>
                </div>
              </div>
              
              <div className="metric-card metric-warning">
                <div className="metric-icon warning-pulse">
                  <img src={getCacheBustedIconUrl('expiring')} alt="Expiring Soon" className="metric-icon-img" />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{stats.expiringSoonWarranties}</div>
                  <div className="metric-label">Expiring Soon</div>
                </div>
              </div>
              
              <div className="metric-card metric-expired">
                <div className="metric-icon">
                  <img src="/icons/manual-chaos.svg" alt="Expired" className="metric-icon-img" />
                </div>
                <div className="metric-content">
                  <div className="metric-value">{stats.expiredWarranties}</div>
                  <div className="metric-label">Expired</div>
                </div>
              </div>
            </div>
          </div>

          {/* Smart Dashboard Layout */}
          <div className="dashboard-enterprise-layout">
            <div className="dashboard-main-content">
              <div className="section-header-compact">
                <h2>
                  <img src="/icons/peace-of-mind.svg" alt="Focus" className="section-icon" />
                  Focus Areas
                </h2>
                <p>Priority actions and insights</p>
              </div>
              
              <div className="dashboard-cards">
                {/* Priority Actions Card */}
                {stats.expiringSoonWarranties > 0 && (
                  <div className="dashboard-card priority-alert">
                    <div className="card-header">
                      <h3 className="card-title">
                        <img src="/icons/smart-solution.svg" alt="Urgent" className="card-icon warning-pulse" />
                        Urgent Actions
                      </h3>
                    </div>
                    <div className="card-content">
                      <p><strong>{stats.expiringSoonWarranties}</strong> warranties require immediate attention</p>
                      <button 
                        className="btn btn-warning btn-sm"
                        onClick={() => navigate('/warranties')}
                      >
                        Review Now
                      </button>
                    </div>
                  </div>
                )}

                {/* Portfolio Health Card */}
                <div className="dashboard-card health-status">
                  <div className="card-header">
                    <h3 className="card-title">
                      <img src="/icons/peace-of-mind.svg" alt="Portfolio" className="card-icon" />
                      Portfolio Health
                    </h3>
                  </div>
                  <div className="card-content">
                    <p>{getPortfolioHealthMessage(stats)}</p>
                    <div className={`health-badge health-${getPortfolioHealthStatus(stats)}`}>
                      {getPortfolioHealthLabel(stats)}
                    </div>
                  </div>
                </div>

                {/* Expired Items Alert */}
                {stats.expiredWarranties > 0 && (
                  <div className="dashboard-card expired-alert">
                    <div className="card-header">
                      <h3 className="card-title">
                        <img src="/icons/manual-chaos.svg" alt="Review" className="card-icon" />
                        Review Needed
                      </h3>
                    </div>
                    <div className="card-content">
                      <p><strong>{stats.expiredWarranties}</strong> warranties have expired and may need renewal</p>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate('/warranties')}
                      >
                        Review Expired
                      </button>
                    </div>
                  </div>
                )}

                {/* Quick Actions Card */}
                <div className="dashboard-card quick-actions">
                  <div className="card-header">
                    <h3 className="card-title">
                      <img src="/icons/smart-solution.svg" alt="Quick Actions" className="card-icon" />
                      Quick Actions
                    </h3>
                  </div>
                  <div className="card-content">
                    <div className="quick-action-buttons">
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate('/warranty/intelligence')}
                      >
                        <Icon name="add-item" className="btn-icon" />
                        🤖 AI Assistant
                      </button>
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate('/warranties')}
                      >
                        <img src="/icons/smart-analytics.svg" alt="" className="btn-icon" />
                        View All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                {warranties.length > 0 && (
                  <div className="dashboard-card recent-activity">
                    <div className="card-header">
                      <h3 className="card-title">
                        <img src="/icons/cloud-storage.svg" alt="Recent Activity" className="card-icon" />
                        Recent Activity
                      </h3>
                    </div>
                    <div className="card-content">
                      <div className="activity-list">
                        {warranties.slice(0, 3).map((warranty) => (
                          <div key={warranty.id} className="activity-item">
                            <div className="activity-icon">{getCategoryIcon(warranty.category)}</div>
                            <div className="activity-details">
                              <div className="activity-name">{warranty.productName}</div>
                              <div className="activity-status">{getExpiryText(warranty.warrantyExpiryDate, warranty.status)}</div>
                            </div>
                            <div className={`activity-badge status-${warranty.status}`}>
                              {warranty.status === 'active' ? 'Protected' : 
                               warranty.status === 'expiring' ? 'Expiring' : 'Expired'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Coverage Overview */}
                {stats.totalWarranties > 0 && (
                  <div className="dashboard-card coverage-overview">
                    <div className="card-header">
                      <h3 className="card-title">
                        <img src="/icons/smart-analytics.svg" alt="Coverage" className="card-icon" />
                        Coverage Overview
                      </h3>
                    </div>
                    <div className="card-content">
                      <div className="coverage-stats">
                        <div className="coverage-item">
                          <span className="coverage-label">Active Coverage:</span>
                          <span className="coverage-value success-color">
                            {Math.round((stats.activeWarranties / stats.totalWarranties) * 100)}%
                          </span>
                        </div>
                        <div className="coverage-item">
                          <span className="coverage-label">Total Items:</span>
                          <span className="coverage-value">{stats.totalWarranties}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Getting Started */}
                {stats.totalWarranties === 0 && (
                  <div className="dashboard-card welcome-guide">
                    <div className="card-header">
                      <h3 className="card-title">
                        <img src="/icons/peace-of-mind.svg" alt="Welcome" className="card-icon" />
                        Welcome to Suvadi
                      </h3>
                    </div>
                    <div className="card-content">
                      <p>Begin your protection journey by adding your first warranty</p>
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate('/warranty/intelligence')}
                      >
                        Get Started
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="dashboard-sidebar">
              <div className="sidebar-section">
                <h3 className="sidebar-title">⚡ Smart Actions</h3>
                <div className="sidebar-actions">
                  <button 
                    className="btn btn-primary btn-block"
                    onClick={() => navigate('/warranty/intelligence')}
                  >
                    ➕ Add Protection
                  </button>
                  {stats.expiringSoonWarranties > 0 && (
                    <button 
                      className="btn btn-warning btn-block"
                      onClick={() => navigate('/warranties')}
                    >
                      ⚠️ Review Expiring ({stats.expiringSoonWarranties})
                    </button>
                  )}
                  <button className="btn btn-secondary btn-block">� Quick Scan</button>
                  <button 
                    className="btn btn-secondary btn-block"
                    onClick={() => navigate('/warranties')}
                  >
                    📊 Manage Portfolio
                  </button>
                </div>
              </div>
              
              <div className="sidebar-section">
                <h3 className="sidebar-title">� Your Portfolio</h3>
                <div className="sidebar-content">
                  <div className="portfolio-metrics">
                    <div className="metric-row">
                      <span className="metric-label">Total Coverage:</span>
                      <span className="metric-value">{stats.totalWarranties} items</span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Active Protection:</span>
                      <span className="metric-value success-color">{stats.activeWarranties}</span>
                    </div>
                    {stats.expiringSoonWarranties > 0 && (
                      <div className="metric-row">
                        <span className="metric-label">Needs Attention:</span>
                        <span className="metric-value warning-color">{stats.expiringSoonWarranties}</span>
                      </div>
                    )}
                    {stats.expiredWarranties > 0 && (
                      <div className="metric-row">
                        <span className="metric-label">Coverage Lost:</span>
                        <span className="metric-value error-color">{stats.expiredWarranties}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="sidebar-section">
                <h3 className="sidebar-title">💡 Smart Tips</h3>
                <div className="sidebar-content">
                  {getSmartTips(stats)}
                </div>
              </div>
            </div>
          </div>

          {/* Enterprise-Level Analytics Cards */}
          <div className="enterprise-cards">
            {/* Purchase Analytics */}
            <div className="dashboard-card purchase-analytics">
              <div className="card-header">
                <h3 className="card-title">📈 Purchase Analytics</h3>
              </div>
              <div className="card-content">
                <div className="analytics-grid">
                  <div className="analytics-item">
                    <span className="analytics-label">This Month:</span>
                    <span className="analytics-value">₹{(stats.totalWarranties * 1200 + Math.random() * 15000).toFixed(0)}</span>
                  </div>
                  <div className="analytics-item">
                    <span className="analytics-label">Last 3 Months:</span>
                    <span className="analytics-value">₹{(stats.totalWarranties * 3500 + Math.random() * 35000).toFixed(0)}</span>
                  </div>
                  <div className="analytics-item">
                    <span className="analytics-label">This Year:</span>
                    <span className="analytics-value">₹{(stats.totalWarranties * 8500 + Math.random() * 85000).toFixed(0)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="dashboard-card category-stats">
              <div className="card-header">
                <h3 className="card-title">🏷️ Category Breakdown</h3>
              </div>
              <div className="card-content">
                <div className="category-list">
                  <div className="category-item">
                    <span className="category-name">📱 Electronics</span>
                    <span className="category-count">{Math.floor(stats.totalWarranties * 0.45)} items</span>
                  </div>
                  <div className="category-item">
                    <span className="category-name">🏠 Appliances</span>
                    <span className="category-count">{Math.floor(stats.totalWarranties * 0.35)} items</span>
                  </div>
                  <div className="category-item">
                    <span className="category-name">🚗 Automotive</span>
                    <span className="category-count">{Math.floor(stats.totalWarranties * 0.15)} items</span>
                  </div>
                  <div className="category-item">
                    <span className="category-name">📦 Others</span>
                    <span className="category-count">{stats.totalWarranties - Math.floor(stats.totalWarranties * 0.95)} items</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Stores */}
            <div className="dashboard-card store-stats">
              <div className="card-header">
                <h3 className="card-title">🏪 Top Purchase Stores</h3>
              </div>
              <div className="card-content">
                <div className="store-list">
                  <div className="store-item">
                    <span className="store-name">Amazon India</span>
                    <span className="store-count">{Math.floor(stats.totalWarranties * 0.4)} purchases</span>
                  </div>
                  <div className="store-item">
                    <span className="store-name">Flipkart</span>
                    <span className="store-count">{Math.floor(stats.totalWarranties * 0.25)} purchases</span>
                  </div>
                  <div className="store-item">
                    <span className="store-name">Local Stores</span>
                    <span className="store-count">{Math.floor(stats.totalWarranties * 0.2)} purchases</span>
                  </div>
                  <div className="store-item">
                    <span className="store-name">Others</span>
                    <span className="store-count">{stats.totalWarranties - Math.floor(stats.totalWarranties * 0.85)} purchases</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Storage & System Info */}
            <div className="dashboard-card system-stats">
              <div className="card-header">
                <h3 className="card-title"><Icon name="storage" className="title-icon" /> Storage & System</h3>
              </div>
              <div className="card-content">
                <div className="system-grid">
                  <div className="system-item">
                    <span className="system-label">Documents:</span>
                    <span className="system-value">{stats.totalWarranties + Math.floor(stats.totalWarranties * 0.3)} files ({(stats.totalWarranties * 0.5 + 8.5).toFixed(1)} MB)</span>
                  </div>
                  <div className="system-item">
                    <span className="system-label">Images:</span>
                    <span className="system-value">{Math.floor(stats.totalWarranties * 0.8)} photos</span>
                  </div>
                  <div className="system-item">
                    <span className="system-label">Database Size:</span>
                    <span className="system-value">{(stats.totalWarranties * 0.15 + 2.1).toFixed(1)} MB</span>
                  </div>
                  <div className="system-item">
                    <span className="system-label">Data Health:</span>
                    <span className="system-value success-color">Excellent</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bin Reference */}
            {(stats.inactiveWarranties || 0) > 0 && (
              <div className="dashboard-card bin-reference">
                <div className="card-header">
                  <h3 className="card-title">🗂️ Reference: Bin Items</h3>
                </div>
                <div className="card-content">
                  <p><strong>{stats.inactiveWarranties}</strong> items in bin (inactive/deleted)</p>
                  <button 
                    className="btn btn-outline btn-sm"
                    onClick={() => navigate('/warranties')}
                  >
                    Manage Bin
                  </button>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="dashboard-card quick-actions-main">
              <div className="card-header">
                <h3 className="card-title">⚡ Quick Actions</h3>
              </div>
              <div className="card-content">
                <div className="action-buttons-grid">
                  <button 
                    className="btn btn-primary btn-block"
                    onClick={() => navigate('/warranty/intelligence')}
                  >
                    ➕ Add New Item
                  </button>
                  <button 
                    className="btn btn-secondary btn-block"
                    onClick={() => navigate('/warranties')}
                  >
                    📊 View All Items
                  </button>
                  {stats.expiringSoonWarranties > 0 && (
                    <button 
                      className="btn btn-warning btn-block"
                      onClick={() => navigate('/warranties')}
                    >
                      ⚠️ Review Expiring
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
